import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const Container = styled.div`
  padding: 2rem;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  font-size: 2.2rem;
  font-weight: 700;
  background: var(--gradient-primary);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const TabsContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 2rem;
`;

const Tab = styled.div`
  padding: 0.8rem 1.5rem;
  background: ${props => props.active ? 'rgba(99, 102, 241, 0.2)' : 'rgba(30, 41, 59, 0.6)'};
  border: 1px solid ${props => props.active ? 'var(--primary)' : 'rgba(255, 255, 255, 0.1)'};
  border-radius: 12px;
  color: ${props => props.active ? 'var(--primary-light)' : 'var(--text-secondary)'};
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(99, 102, 241, 0.15);
    border-color: var(--primary-light);
    color: var(--primary-light);
  }
`;

const ClaimsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 350px;
  gap: 1.5rem;
`;

const ClaimsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const ClaimCard = styled(motion.div)`
  background: rgba(30, 41, 59, 0.6);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 1.5rem;
  cursor: pointer;
  
  ${props => props.selected && `
    border-color: var(--primary);
    background: rgba(99, 102, 241, 0.1);
  `}
  
  &:hover {
    background: rgba(30, 41, 59, 0.8);
  }
`;

const ClaimHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;

const ClaimId = styled.div`
  font-weight: 600;
  font-size: 1.1rem;
`;

const ClaimStatus = styled.div`
  padding: 0.3rem 0.8rem;
  border-radius: 50px;
  font-size: 0.75rem;
  font-weight: 500;
  background: ${props => 
    props.status === 'Approved' ? 'rgba(16, 185, 129, 0.2)' : 
    props.status === 'Pending' ? 'rgba(245, 158, 11, 0.2)' : 
    props.status === 'Under Review' ? 'rgba(59, 130, 246, 0.2)' : 
    props.status === 'Denied' ? 'rgba(239, 68, 68, 0.2)' : 
    'rgba(99, 102, 241, 0.2)'
  };
  color: ${props => 
    props.status === 'Approved' ? 'var(--success)' : 
    props.status === 'Pending' ? 'var(--warning)' : 
    props.status === 'Under Review' ? '#3b82f6' : 
    props.status === 'Denied' ? 'var(--error)' : 
    'var(--primary)'
  };
`;

const ClaimInfo = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-bottom: 1rem;
`;

const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
`;

const InfoLabel = styled.div`
  font-size: 0.85rem;
  color: var(--text-secondary);
  margin-bottom: 0.3rem;
`;

const InfoValue = styled.div`
  font-weight: 500;
`;

const ClaimDescription = styled.div`
  margin-bottom: 1rem;
  font-size: 0.95rem;
  color: var(--text-secondary);
`;

const ClaimFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ClaimDate = styled.div`
  font-size: 0.85rem;
  color: var(--text-secondary);
`;

const AiBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 0.3rem;
  font-size: 0.85rem;
  color: var(--primary-light);
`;

const DetailPanel = styled.div`
  background: rgba(30, 41, 59, 0.6);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  height: 100%;
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
`;

const DetailHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const DetailTitle = styled.h2`
  font-size: 1.3rem;
  font-weight: 600;
`;

const CloseButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(30, 41, 59, 0.8);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: var(--text-primary);
  cursor: pointer;
  
  &:hover {
    background: rgba(99, 102, 241, 0.2);
  }
`;

const DetailContent = styled.div`
  flex: 1;
  overflow-y: auto;
`;

const DetailSection = styled.div`
  margin-bottom: 2rem;
`;

const SectionTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 1rem;
  color: var(--text-secondary);
`;

const DocumentList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
`;

const Document = styled.div`
  display: flex;
  align-items: center;
  padding: 0.8rem;
  border-radius: 8px;
  background: rgba(30, 41, 59, 0.4);
  gap: 0.8rem;
  
  &:hover {
    background: rgba(30, 41, 59, 0.8);
  }
`;

const DocumentIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: rgba(99, 102, 241, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
`;

const DocumentInfo = styled.div`
  flex: 1;
`;

const DocumentName = styled.div`
  font-size: 0.9rem;
  font-weight: 500;
  margin-bottom: 0.2rem;
`;

const DocumentMeta = styled.div`
  font-size: 0.8rem;
  color: var(--text-secondary);
`;

const Timeline = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    left: 12px;
    top: 8px;
    bottom: 8px;
    width: 2px;
    background: rgba(255, 255, 255, 0.1);
  }
`;

const TimelineItem = styled.div`
  display: flex;
  gap: 1rem;
`;

const TimelineDot = styled.div`
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: ${props => props.color || 'var(--primary)'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
  z-index: 1;
`;

const TimelineContent = styled.div`
  flex: 1;
`;

const TimelineHeader = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.3rem;
`;

const TimelineTitle = styled.div`
  font-weight: 500;
  font-size: 0.95rem;
`;

const TimelineTime = styled.div`
  font-size: 0.8rem;
  color: var(--text-secondary);
`;

const TimelineDescription = styled.div`
  font-size: 0.85rem;
  color: var(--text-secondary);
`;

const AiInsightBox = styled.div`
  background: rgba(99, 102, 241, 0.1);
  border: 1px solid rgba(99, 102, 241, 0.3);
  border-radius: 12px;
  padding: 1.2rem;
  margin-bottom: 1.5rem;
`;

const AiInsightHeader = styled.div`
  display: flex;
  gap: 0.7rem;
  align-items: center;
  margin-bottom: 0.8rem;
`;

const AiIcon = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: var(--gradient-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
`;

const AiTitle = styled.div`
  font-weight: 600;
  font-size: 1rem;
`;

const AiContent = styled.div`
  font-size: 0.95rem;
  line-height: 1.5;
  color: var(--text-secondary);
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: auto;
  padding-top: 1.5rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
`;

const Button = styled(motion.button)`
  flex: 1;
  padding: 0.8rem;
  background: ${props => props.primary ? 'var(--gradient-primary)' : 'rgba(30, 41, 59, 0.8)'};
  border: 1px solid ${props => props.primary ? 'transparent' : 'rgba(255, 255, 255, 0.1)'};
  border-radius: 8px;
  color: var(--text-primary);
  font-weight: 500;
  cursor: pointer;
  
  &:hover {
    background: ${props => props.primary ? 'var(--gradient-primary)' : 'rgba(99, 102, 241, 0.2)'};
    border-color: ${props => props.primary ? 'transparent' : 'var(--primary-light)'};
  }
`;

const claimsData = [
  {
    id: 'CLM-2023-0078',
    status: 'Pending',
    policyId: 'POL-2023-0123',
    client: 'TechStart Inc.',
    type: 'Data Breach',
    amount: '$175,000',
    date: '2023-03-12',
    description: 'Claim for data breach incident affecting customer database. Preliminary investigation indicates unauthorized access to personal information of approximately 5,000 customers.',
    aiAssisted: true
  },
  {
    id: 'CLM-2023-0065',
    status: 'Under Review',
    policyId: 'POL-2023-0045',
    client: 'Acme Corporation',
    type: 'Property Damage',
    amount: '$85,400',
    date: '2023-03-05',
    description: 'Claim for water damage to warehouse facility following sprinkler system failure. Damage primarily affecting inventory storage area and electrical systems.',
    aiAssisted: true
  },
  {
    id: 'CLM-2023-0047',
    status: 'Approved',
    policyId: 'POL-2023-0215',
    client: 'Sarah Johnson',
    type: 'Home Theft',
    amount: '$8,750',
    date: '2023-02-18',
    description: 'Claim for theft of personal property from residence. Items include electronics, jewelry, and cash. Police report filed on February 18, 2023.',
    aiAssisted: false
  },
  {
    id: 'CLM-2023-0036',
    status: 'Denied',
    policyId: 'POL-2023-0267',
    client: 'Robert Smith',
    type: 'Vehicle Collision',
    amount: '$12,300',
    date: '2023-02-10',
    description: 'Claim for vehicle damage resulting from collision. Investigation determined policyholder was operating vehicle under influence of alcohol, which is excluded under policy terms.',
    aiAssisted: true
  },
  {
    id: 'CLM-2023-0029',
    status: 'Approved',
    policyId: 'POL-2022-1876',
    client: 'Liberty Logistics',
    type: 'Vehicle Damage',
    amount: '$18,500',
    date: '2023-01-30',
    description: 'Claim for damage to delivery truck following collision with road debris. Damage affecting front axle, bumper, and radiator system.',
    aiAssisted: false
  }
];

const selectedClaimTimeline = [
  {
    icon: '📝',
    title: 'Claim Created',
    time: 'Mar 12, 2023 - 09:45 AM',
    description: 'Claim submitted through online portal by John Miller (CTO).',
    color: 'var(--primary)'
  },
  {
    icon: '🤖',
    title: 'AI Initial Assessment',
    time: 'Mar 12, 2023 - 09:47 AM',
    description: 'AI analyzed claim details and categorized as "Data Breach" with high severity rating.',
    color: 'var(--accent)'
  },
  {
    icon: '📊',
    title: 'Risk Analysis Completed',
    time: 'Mar 13, 2023 - 11:30 AM',
    description: 'AI completed analysis of potential financial impact and liability exposure.',
    color: 'var(--accent)'
  },
  {
    icon: '👤',
    title: 'Assigned to Human Agent',
    time: 'Mar 13, 2023 - 02:15 PM',
    description: 'Claim assigned to Sarah Williams (Cyber Claims Specialist).',
    color: 'var(--primary)'
  },
  {
    icon: '🔍',
    title: 'Additional Documentation Requested',
    time: 'Mar 14, 2023 - 10:20 AM',
    description: 'Request sent for incident report, affected data details, and remediation steps taken.',
    color: 'var(--warning)'
  }
];

const selectedClaimDocuments = [
  {
    icon: '📄',
    name: 'Incident Report.pdf',
    date: 'Mar 12, 2023',
    size: '2.4 MB'
  },
  {
    icon: '📊',
    name: 'Data Breach Assessment.xlsx',
    date: 'Mar 13, 2023',
    size: '1.8 MB'
  },
  {
    icon: '📋',
    name: 'Customer Notification Template.docx',
    date: 'Mar 14, 2023',
    size: '540 KB'
  },
  {
    icon: '📑',
    name: 'Remediation Plan.pdf',
    date: 'Mar 15, 2023',
    size: '3.2 MB'
  }
];

const Claims = () => {
  const [activeTab, setActiveTab] = useState('all');
  const [selectedClaim, setSelectedClaim] = useState(claimsData[0]);
  
  return (
    <Container>
      <Header>
        <Title>Claims Management</Title>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          style={{
            padding: '0.7rem 1.5rem',
            background: 'var(--gradient-primary)',
            border: 'none',
            borderRadius: '8px',
            color: 'white',
            fontWeight: '500',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}
        >
          ➕ New Claim
        </motion.button>
      </Header>
      
      <TabsContainer>
        <Tab active={activeTab === 'all'} onClick={() => setActiveTab('all')}>All Claims</Tab>
        <Tab active={activeTab === 'pending'} onClick={() => setActiveTab('pending')}>Pending</Tab>
        <Tab active={activeTab === 'review'} onClick={() => setActiveTab('review')}>Under Review</Tab>
        <Tab active={activeTab === 'approved'} onClick={() => setActiveTab('approved')}>Approved</Tab>
        <Tab active={activeTab === 'denied'} onClick={() => setActiveTab('denied')}>Denied</Tab>
        <Tab active={activeTab === 'ai'} onClick={() => setActiveTab('ai')}>AI Assisted</Tab>
      </TabsContainer>
      
      <ClaimsGrid>
        <ClaimsList>
          {claimsData.map((claim, index) => (
            <ClaimCard 
              key={claim.id} 
              selected={selectedClaim.id === claim.id}
              onClick={() => setSelectedClaim(claim)}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <ClaimHeader>
                <ClaimId>{claim.id}</ClaimId>
                <ClaimStatus status={claim.status}>{claim.status}</ClaimStatus>
              </ClaimHeader>
              
              <ClaimInfo>
                <InfoItem>
                  <InfoLabel>Client</InfoLabel>
                  <InfoValue>{claim.client}</InfoValue>
                </InfoItem>
                <InfoItem>
                  <InfoLabel>Policy ID</InfoLabel>
                  <InfoValue>{claim.policyId}</InfoValue>
                </InfoItem>
                <InfoItem>
                  <InfoLabel>Claim Type</InfoLabel>
                  <InfoValue>{claim.type}</InfoValue>
                </InfoItem>
                <InfoItem>
                  <InfoLabel>Amount</InfoLabel>
                  <InfoValue>{claim.amount}</InfoValue>
                </InfoItem>
              </ClaimInfo>
              
              <ClaimDescription>{claim.description}</ClaimDescription>
              
              <ClaimFooter>
                <ClaimDate>Filed: {claim.date}</ClaimDate>
                {claim.aiAssisted && (
                  <AiBadge>
                    <span>🤖</span>
                    <span>AI Assisted</span>
                  </AiBadge>
                )}
              </ClaimFooter>
            </ClaimCard>
          ))}
        </ClaimsList>
        
        <DetailPanel>
          <DetailHeader>
            <DetailTitle>Claim Details</DetailTitle>
            <CloseButton>✕</CloseButton>
          </DetailHeader>
          
          <DetailContent>
            <AiInsightBox>
              <AiInsightHeader>
                <AiIcon>🤖</AiIcon>
                <AiTitle>AI Agent Insights</AiTitle>
              </AiInsightHeader>
              <AiContent>
                Based on analysis of similar data breach claims and policy terms, there is an 82% probability of approval with recommended coverage of $145,000. Response time is critical to minimize further customer data exposure and potential liability.
              </AiContent>
            </AiInsightBox>
            
            <DetailSection>
              <SectionTitle>Timeline</SectionTitle>
              <Timeline>
                {selectedClaimTimeline.map((item, index) => (
                  <TimelineItem key={index}>
                    <TimelineDot color={item.color}>{item.icon}</TimelineDot>
                    <TimelineContent>
                      <TimelineHeader>
                        <TimelineTitle>{item.title}</TimelineTitle>
                        <TimelineTime>{item.time}</TimelineTime>
                      </TimelineHeader>
                      <TimelineDescription>{item.description}</TimelineDescription>
                    </TimelineContent>
                  </TimelineItem>
                ))}
              </Timeline>
            </DetailSection>
            
            <DetailSection>
              <SectionTitle>Documents</SectionTitle>
              <DocumentList>
                {selectedClaimDocuments.map((doc, index) => (
                  <Document key={index}>
                    <DocumentIcon>{doc.icon}</DocumentIcon>
                    <DocumentInfo>
                      <DocumentName>{doc.name}</DocumentName>
                      <DocumentMeta>Added: {doc.date} • {doc.size}</DocumentMeta>
                    </DocumentInfo>
                  </Document>
                ))}
              </DocumentList>
            </DetailSection>
          </DetailContent>
          
          <ButtonGroup>
            <Button
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
            >
              Request Information
            </Button>
            <Button 
              primary
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
            >
              Process Claim
            </Button>
          </ButtonGroup>
        </DetailPanel>
      </ClaimsGrid>
    </Container>
  );
};

export default Claims;
